import { Link } from "react-router-dom";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { firstOfThisMonth, MRP_FORECAST_API_LOCATION_PARAM, type MrpForecastResult } from "@/lib/mrp";

const SECTIONS: { to: string; title: string; description: string }[] = [
  {
    to: "/mrp/forecast",
    title: "Forecast",
    description:
      "Rolling demand vs. on-hand stock across SGS, Suntronic, and Kodiak. The first load can take several seconds; after that, recent results stay cached while the server recalculates.",
  },
  {
    to: "/mrp/ghost-build-orders",
    title: "Ghost build orders",
    description:
      "Enter multiple finished-good build lines, preview incremental component shortages against the live forecast, and pin what-if demand without changing the build plan.",
  },
  {
    to: "/mrp/planning",
    title: "Planning",
    description:
      "Sales lines by calendar quarter — FI-*-TLA-* / TLA uses BoM slot `topLevel` (`process_boms` `variable_name`).",
  },
  {
    to: "/mrp/build-plan",
    title: "Build plan",
    description: "Monthly build quantities per assembly and location.",
  },
  {
    to: "/mrp/assemblies",
    title: "Assemblies",
    description: "Logical assemblies and per-location SKU mappings for the forecast engine.",
  },
  {
    to: "/mrp/skus",
    title: "SKUs",
    description:
      "Planning mode on public.sku (MRP, reorder point, build plan, or N/A). Forecast can filter to MRP-only SKUs.",
  },
  {
    to: "/mrp/overstock",
    title: "Overstock",
    description:
      "MRP SKUs with surplus inventory versus the build-plan forecast — months of supply, on-order quantity, and projected overstock value.",
  },
  {
    to: "/mrp/safety-stock-alerts",
    title: "Safety stock alerts",
    description:
      "Reorder-point SKUs whose NET stock at a site has fallen below the site reserve (Megaventory snapshot).",
  },
  {
    to: "/mrp/aml",
    title: "AML",
    description: "Approved material list (public.approved_components).",
  },
  {
    to: "/mrp/purchase-orders",
    title: "Purchase orders",
    description: "Hub purchase orders and a link to create new purchase orders.",
  },
  {
    to: "/mrp/incoming-parts",
    title: "Incoming parts",
    description: "Open purchase lines and manual arrivals.",
  },
  {
    to: "/mrp/transmute",
    title: "Transmute",
    description:
      "Move approved AML alternates into one target SKU in Megaventory with valuation-safe unit conversions (pounds to grams and similar).",
  },
  {
    to: "/mrp/settings",
    title: "Settings",
    description: "MRP application settings and knobs.",
  },
];

/** Matches Forecast.tsx defaults so prefetch hits the same query key. */
function prefetchDefaultForecast(queryClient: QueryClient): void {
  const startMonth = firstOfThisMonth();
  const horizon = 12;
  void queryClient.prefetchQuery({
    queryKey: ["mrp", "forecast", { startMonth, horizon, locations: MRP_FORECAST_API_LOCATION_PARAM }],
    queryFn: () =>
      apiFetch<MrpForecastResult>("/api/mrp/forecast", {
        query: {
          start_month: startMonth,
          horizon,
          location: MRP_FORECAST_API_LOCATION_PARAM,
        },
      }),
    staleTime: 5 * 60_000,
  });
}

export function MrpHome() {
  const queryClient = useQueryClient();

  return (
    <div className="space-y-6">
      <header>
        <PageTitleWithHelp hoverHint="Choose a section below. Navigation stays responsive while heavy pages load or refresh in the background.">
          Material requirements planning
        </PageTitleWithHelp>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {SECTIONS.map((section) => (
          <Card key={section.to} className="h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                <Link
                  to={section.to}
                  className="text-brand-700 hover:underline"
                  onMouseEnter={() => {
                    if (section.to === "/mrp/forecast") prefetchDefaultForecast(queryClient);
                  }}
                  onFocus={() => {
                    if (section.to === "/mrp/forecast") prefetchDefaultForecast(queryClient);
                  }}
                >
                  {section.title}
                </Link>
              </CardTitle>
            </CardHeader>
            <CardBody className="pt-0">
              <p className="text-sm text-slate-600">{section.description}</p>
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
}
